// src/pages/admin/Professionals.jsx — Com painel lateral de edição
import React, { useState } from 'react';
import { useApp } from 'contexts/AppContext';
import { useAuth } from 'contexts/AuthContext';
import { authService, usersService, proceduresService } from 'infrastructure/firebase';
import { Button, Badge, Avatar, EmptyState, ChipMultiSelect, PhotoUpload } from 'shared/components/ui';
import { useMobile } from 'shared/hooks/useMobile';

function getProcedureProfessionalIds(proc) {
  if (proc.professionals?.length) return [...proc.professionals];
  return proc.professionalId ? [proc.professionalId] : [];
}

async function syncProfessionalProcedures(proId, selectedIds, allProcedures) {
  const selected = new Set(selectedIds);
  const updates = allProcedures.map(async (proc) => {
    const current = new Set(getProcedureProfessionalIds(proc));
    const shouldHave = selected.has(proc.id);
    if (shouldHave) current.add(proId);
    else current.delete(proId);

    const next = Array.from(current);
    const prev = getProcedureProfessionalIds(proc);
    if (next.length === prev.length && next.every(id => prev.includes(id))) return;

    await proceduresService.update(proc.id, {
      professionals: next,
      professionalId: next[0] || null,
    });
  });
  await Promise.all(updates);
}

const AVATAR_COLORS = [
  { id:'rose',     label:'Rosa',     color:'rose' },
  { id:'lavender', label:'Lavanda',  color:'lavender' },
  { id:'mint',     label:'Menta',    color:'mint' },
  { id:'peach',    label:'Pêssego',  color:'peach' },
  { id:'blue',     label:'Azul',     color:'blue' },
  { id:'green',    label:'Verde',    color:'green' },
  { id:'mixed',    label:'Misto',    color:'mixed' },
];

// ── Professional Card no Grid ──────────────────────────────────
function ProfessionalCard({ pro, procedures, onEdit }) {
  const procs = (procedures || []).filter(p => {
    const profIds = p.professionals || (p.professionalId ? [p.professionalId] : []);
    return profIds.includes(pro.id);
  });
  
  return (
    <div style={{
      background:'#fff', border:'1px solid #E8E5EC', borderRadius:20, overflow:'hidden',
      fontFamily:"'DM Sans',sans-serif", display:'flex', flexDirection:'column',
    }}>
      <div style={{
        padding:'22px 18px 14px', display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center',
        background:'linear-gradient(180deg, #FAF7FB, #fff)',
      }}>
        {pro.photo
          ? <div style={{ width:72, height:72, borderRadius:'50%', background:`url(${pro.photo}) center/cover`, marginBottom:10 }} />
          : <div style={{ marginBottom:10 }}><Avatar name={pro.name} color={pro.avatarColor || 'blue'} size="xl" /></div>}
        <div style={{ fontWeight:600, fontSize:15, color:'#3D3447' }}>{pro.name}</div>
        <div style={{ fontSize:12, color:'#A8A1B0', marginTop:2 }}>{pro.specialty || 'Profissional'}</div>
        {pro.active !== false && (
          <Badge variant="success" style={{ marginTop:6, fontSize:10 }}>● Ativo</Badge>
        )}
      </div>
      <div style={{ padding:'14px 18px 16px', display:'flex', flexDirection:'column', gap:10, borderTop:'1px solid #E8E5EC' }}>
        <div style={{ fontSize:10, fontWeight:600, color:'#A8A1B0', textTransform:'uppercase', letterSpacing:1 }}>
          Realiza {procs.length} {procs.length === 1 ? 'procedimento' : 'procedimentos'}
        </div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
          {procs.slice(0,3).map(p => (
            <span key={p.id} style={{ fontSize:11, padding:'3px 9px', background:'#F0EDF3', color:'#6B6070', borderRadius:20, fontWeight:500 }}>
              {p.name}
            </span>
          ))}
          {procs.length > 3 && <span style={{ fontSize:11, color:'#A8A1B0', padding:'3px 9px' }}>+{procs.length - 3}</span>}
          {procs.length === 0 && <span style={{ fontSize:11, color:'#A8A1B0', fontStyle:'italic' }}>Nenhum procedimento associado</span>}
        </div>
        <Button variant="secondary" size="sm" onClick={() => onEdit(pro)} style={{ marginTop:6 }}>Editar perfil</Button>
      </div>
    </div>
  );
}

// ── Painel Lateral de Edição ────────────────────────────────────
function ProfessionalFormPanel({ pro, procedures, onClose, showToast, estabelecimentoId }) {
  const isMobile = useMobile();
  const isNew = !pro || !pro.id;
  const [form, setForm] = useState(() => ({
    name: pro?.name || '',
    specialty: pro?.specialty || '',
    phone: pro?.phone || '',
    email: pro?.email || '',
    photo: pro?.photo || null,
    avatarColor: pro?.avatarColor || 'lavender',
    procedures: (procedures || [])
      .filter(p => {
        const profIds = p.professionals || (p.professionalId ? [p.professionalId] : []);
        return profIds.includes(pro?.id);
      })
      .map(p => p.id) || [],
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name) {
      showToast('Preencha o nome do profissional.', 'warning');
      return;
    }
    if (isNew && !form.email) {
      showToast('Preencha o e-mail para criar o login do profissional.', 'warning');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        // Cria conta de autenticação + documento no Firestore
        const tempPass = Math.random().toString(36).slice(-8) + 'A1!';
        const newUser  = await authService.register({
          email:            form.email,
          password:         tempPass,
          name:             form.name,
          role:             'professional',
          phone:            form.phone || '',
          specialty:        form.specialty || '',
          estabelecimentoId: estabelecimentoId || null,
        });
        // Salva campos extras que register não inclui (foto e cor do avatar)
        if (newUser?.uid) {
          await usersService.update(newUser.uid, {
            avatarColor: form.avatarColor,
            ...(form.photo ? { photo: form.photo } : {}),
          });
          await syncProfessionalProcedures(newUser.uid, form.procedures, procedures);
        }
        showToast(`"${form.name}" cadastrado. Senha provisória: ${tempPass}`);
      } else {
        await usersService.update(pro.id, {
          name:        form.name,
          specialty:   form.specialty,
          phone:       form.phone,
          email:       form.email,
          photo:       form.photo,
          avatarColor: form.avatarColor,
          role:        'professional',
          active:      true,
        });
        await syncProfessionalProcedures(pro.id, form.procedures, procedures);
        showToast(`"${form.name}" foi atualizado`);
      }
      onClose();
    } catch (e) {
      const msg = e.code === 'auth/email-already-in-use'
        ? 'Este e-mail já está cadastrado. Use outro ou redefina a senha.'
        : e.code === 'auth/invalid-email'
        ? 'E-mail inválido.'
        : e.code === 'auth/weak-password'
        ? 'Senha muito fraca (mínimo 6 caracteres).'
        : 'Erro ao salvar profissional.';
      showToast(msg, 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Excluir "${pro.name}"?`)) return;
    try {
      await usersService.delete(pro.id);
      showToast(`"${pro.name}" foi excluído`);
      onClose();
    } catch {
      showToast('Erro ao excluir.', 'danger');
    }
  };

  return (
    <>
      {/* Overlay */}
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(61,52,71,0.32)',
        backdropFilter:'blur(6px)', zIndex:150 }} />
      
      {/* Side Panel */}
      <div style={{
        position:'fixed', top:0, right:0, bottom:0, width:'min(540px, 100vw)', background:'#fff',
        zIndex:151, boxShadow:'-12px 0 40px rgba(74,63,88,0.18)', display:'flex', flexDirection:'column',
        fontFamily:"'DM Sans',sans-serif",
      }}>
        {/* Header */}
        <div style={{ padding:'22px 28px', borderBottom:'1px solid #E8E5EC',
          display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:24, color:'#3D3447' }}>
              {isNew ? 'Novo profissional' : 'Editar profissional'}
            </div>
            <div style={{ fontSize:12, color:'#A8A1B0', marginTop:2 }}>
              {isNew ? 'Cadastre um novo membro da equipe' : `Editando "${pro.name}"`}
            </div>
          </div>
          <button onClick={onClose} style={{ background:'#F0EDF3', border:'none', cursor:'pointer',
            width:32, height:32, borderRadius:8, color:'#6B6070', fontSize:18 }}>×</button>
        </div>

        {/* Body — scroll */}
        <div style={{ padding:'24px 28px', flex:1, overflowY:'auto' }}>
          <div style={{ marginBottom:18 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>
              Foto
            </label>
            <div style={{ fontSize:11, color:'#A8A1B0', marginBottom:8 }}>
              Avatar exibido na agenda, dashboard e para o cliente.
            </div>
            <PhotoUpload value={form.photo} onChange={v => set('photo', v)} emoji="👤" />
          </div>

          <div style={{ marginBottom:18 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>
              Nome completo <span style={{ color:'#C9929A' }}>*</span>
            </label>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ex.: Mariana Silva"
              style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #E8E5EC', borderRadius:12, fontSize:14, outline:'none', boxSizing:'border-box', color:'#3D3447', background:'#fff' }} />
          </div>

          <div style={{ marginBottom:18 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>
              Especialidade
            </label>
            <div style={{ fontSize:11, color:'#A8A1B0', marginBottom:8 }}>
              Texto curto exibido abaixo do nome.
            </div>
            <input value={form.specialty} onChange={e => set('specialty', e.target.value)} placeholder="Ex.: Esteticista facial"
              style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #E8E5EC', borderRadius:12, fontSize:14, outline:'none', boxSizing:'border-box', color:'#3D3447', background:'#fff' }} />
          </div>

          <div style={{ marginBottom:18 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>
              Cor do avatar (gradiente)
            </label>
            <div style={{ fontSize:11, color:'#A8A1B0', marginBottom:8 }}>
              Usado quando não há foto.
            </div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {AVATAR_COLORS.map(c => (
                <div key={c.id} onClick={() => set('avatarColor', c.id)}
                  style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:5, cursor:'pointer' }}>
                  <div style={{ position:'relative' }}>
                    <Avatar name={form.name || 'A B'} color={c.color} size="md" />
                    {form.avatarColor === c.id && <div style={{ position:'absolute', inset:-3, border:'2px solid #6B5876', borderRadius:'50%' }} />}
                  </div>
                  <span style={{ fontSize:10, color: form.avatarColor === c.id ? '#3D3447' : '#A8A1B0' }}>{c.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:14, marginBottom:18 }}>
            <div>
              <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>
                Telefone
              </label>
              <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(11) 99000-0000"
                style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #E8E5EC', borderRadius:12, fontSize:14, outline:'none', boxSizing:'border-box', color:'#3D3447', background:'#fff' }} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>
                E-mail
              </label>
              <input value={form.email} onChange={e => set('email', e.target.value)} type="email" placeholder="email@dominio.com"
                style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #E8E5EC', borderRadius:12, fontSize:14, outline:'none', boxSizing:'border-box', color:'#3D3447', background:'#fff' }} />
            </div>
          </div>

          <hr style={{ border:'none', borderTop:'1px solid #E8E5EC', margin:'8px 0 18px' }} />

          <div style={{ marginBottom:18 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>
              Procedimentos que realiza
            </label>
            <div style={{ fontSize:11, color:'#A8A1B0', marginBottom:8 }}>
              O profissional só aparecerá no agendamento dos procedimentos marcados aqui.
            </div>
            <ChipMultiSelect
              options={(procedures || []).map(p => ({ id:p.id, label:p.name }))}
              value={form.procedures}
              onChange={v => set('procedures', v)}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding:'16px 28px', borderTop:'1px solid #E8E5EC', background:'#FAF7FB',
          display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0, gap:12 }}>
          {!isNew ? (
            <Button variant="ghost" size="sm" onClick={handleDelete} style={{ color:'#7A3C44' }}>
              🗑 Excluir
            </Button>
          ) : <span />}
          <div style={{ display:'flex', gap:10 }}>
            <Button variant="secondary" onClick={onClose}>Cancelar</Button>
            <Button variant="primary" disabled={saving} loading={saving} onClick={handleSave}>
              {isNew ? 'Cadastrar profissional' : 'Salvar alterações'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Main View ────────────────────────────────────────────────────
export default function AdminProfessionals() {
  const { professionals, procedures, showToast } = useApp();
  const { userProfile } = useAuth();
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  const filtered = professionals.filter(p => !search || p.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ fontFamily:"'DM Sans',sans-serif" }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:32, color:'#3D3447' }}>Profissionais</div>
          <div style={{ fontSize:13, color:'#A8A1B0', marginTop:2 }}>{professionals.length} cadastrados</div>
        </div>
        <Button variant="primary" onClick={() => setEditing({})}>+ Novo profissional</Button>
      </div>

      {/* Search */}
      <div style={{ display:'flex', gap:10, marginBottom:20, alignItems:'center', flexWrap:'wrap' }}>
        <div style={{ position:'relative', flex:1, minWidth:240, maxWidth:380 }}>
          <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#A8A1B0', fontSize:14 }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar profissional..."
            style={{ width:'100%', padding:'9px 14px 9px 36px', border:'1.5px solid #E8E5EC',
              borderRadius:12, fontSize:14, outline:'none', boxSizing:'border-box',
              color:'#3D3447', background:'#fff' }} />
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <EmptyState 
          icon="👩‍⚕️" 
          title="Nenhum profissional encontrado" 
          text="Tente outra busca ou cadastre um novo profissional."
        />
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))', gap:16 }}>
          {filtered.map(p => (
            <ProfessionalCard key={p.id} pro={p} procedures={procedures} onEdit={setEditing} />
          ))}
        </div>
      )}

      {/* Side panel */}
      {editing && (
        <ProfessionalFormPanel
          pro={editing}
          procedures={procedures}
          showToast={showToast}
          onClose={() => setEditing(null)}
          estabelecimentoId={userProfile?.estabelecimentoId || null}
        />
      )}
    </div>
  );
}
